import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import {
  distributeDepartmentStock,
  ensureDepartmentWarehouse,
  getDepartmentStockOverview,
  getDepartmentStore,
  getFactoryStore,
  getInventoryMovements,
  getLotBatchCodesWithStock,
  listDepartmentMaster,
  listDepartmentWarehouses,
} from "services/factoryDepartmentStockApi"
import {
  DistributeStockInput,
  GetLotBatchCodesWithStockInput,
  InventoryMovementInput,
  ResponseDepartmentStockOverview,
  ResponseDepartmentStore,
  ResponseDistributeDepartmentStock,
  ResponseEnsureDepartmentWarehouse,
  ResponseFactoryStore,
  ResponseInventoryMovements,
  ResponseListDepartmentMaster,
  ResponseListDepartmentWarehouses,
  ResponseLotBatchCodesWithStock,
} from "services/interface"

export const useListDepartmentMaster = (): UseQueryResult<
  ResponseListDepartmentMaster,
  Error
> => {
  return useQuery<ResponseListDepartmentMaster, Error>({
    queryKey: ["department_master_list"],
    queryFn: () => listDepartmentMaster(),
    staleTime: 1000 * 60 * 10,
  })
}

export const useGetFactoryStore = (): UseQueryResult<
  ResponseFactoryStore,
  Error
> => {
  return useQuery<ResponseFactoryStore, Error>({
    queryKey: ["factory_store"],
    queryFn: () => getFactoryStore(),
  })
}

export const useGetDepartmentStore = (
  departmentId: string | null | undefined
): UseQueryResult<ResponseDepartmentStore, Error> => {
  return useQuery<ResponseDepartmentStore, Error>({
    queryKey: ["department_store", departmentId],
    queryFn: () => getDepartmentStore(String(departmentId)),
    enabled: !!departmentId,
  })
}

export const useGetInventoryMovements = (
  warehouseId: string | null | undefined,
  filter: InventoryMovementInput
): UseQueryResult<ResponseInventoryMovements, Error> => {
  return useQuery<ResponseInventoryMovements, Error>({
    queryKey: ["department_stock_movements", warehouseId, filter],
    queryFn: () =>
      getInventoryMovements({ ...filter, warehouse_id: String(warehouseId) }),
    enabled: !!warehouseId,
  })
}

export const useGetDepartmentStockOverview = (
  factoryId: string | null | undefined
): UseQueryResult<ResponseDepartmentStockOverview, Error> => {
  return useQuery<ResponseDepartmentStockOverview, Error>({
    queryKey: ["department_stock_overview", factoryId],
    queryFn: () => getDepartmentStockOverview(String(factoryId)),
    enabled: !!factoryId,
  })
}

export const useListDepartmentWarehouses = (
  factoryId: string | null | undefined
): UseQueryResult<ResponseListDepartmentWarehouses, Error> => {
  return useQuery<ResponseListDepartmentWarehouses, Error>({
    queryKey: ["department_warehouses", factoryId],
    queryFn: () => listDepartmentWarehouses(String(factoryId)),
    enabled: !!factoryId,
  })
}

export const useEnsureDepartmentWarehouse = (): UseMutationResult<
  ResponseEnsureDepartmentWarehouse,
  Error,
  string,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation<ResponseEnsureDepartmentWarehouse, Error, string, unknown>(
    {
      mutationFn: (departmentMasterId: string) =>
        ensureDepartmentWarehouse(departmentMasterId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["factory_store"] })
        queryClient.invalidateQueries({ queryKey: ["department_warehouses"] })
        queryClient.invalidateQueries({
          queryKey: ["department_stock_overview"],
        })
      },
    }
  )
}

export const useLotBatchCodesWithStock = (
  params: GetLotBatchCodesWithStockInput | null
): UseQueryResult<ResponseLotBatchCodesWithStock, Error> => {
  return useQuery<ResponseLotBatchCodesWithStock, Error>({
    queryKey: [
      "lot_batch_codes_with_stock",
      params?.warehouse_id,
      params?.product_id,
      params?.product_sku_id,
      params?.sku_variant_id,
    ],
    queryFn: () =>
      getLotBatchCodesWithStock(params as GetLotBatchCodesWithStockInput),
    enabled: !!params?.warehouse_id && !!params?.product_id,
    staleTime: 1000 * 60,
  })
}

export const useDistributeDepartmentStock = (): UseMutationResult<
  ResponseDistributeDepartmentStock,
  Error,
  DistributeStockInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation<
    ResponseDistributeDepartmentStock,
    Error,
    DistributeStockInput,
    unknown
  >({
    mutationFn: (input: DistributeStockInput) =>
      distributeDepartmentStock(input),
    onSuccess: () => {
      // บังคับ refetch /factory/department-stock/factory-store ทั้ง active + inactive
      // เพื่อให้รายการคลัง (WarehouseListTab) อัปเดตหลังโอนสำเร็จเสมอ
      queryClient.invalidateQueries({
        queryKey: ["factory_store"],
        refetchType: "all",
      })
      queryClient.invalidateQueries({ queryKey: ["department_warehouses"] })
      queryClient.invalidateQueries({ queryKey: ["department_stock_overview"] })
      queryClient.invalidateQueries({ queryKey: ["department_store"] })
      queryClient.invalidateQueries({
        queryKey: ["department_stock_movements"],
      })
      // lot ที่เลือกถูกตัดสต็อก → ให้ dropdown lot โหลดค่าคงเหลือใหม่
      queryClient.invalidateQueries({
        queryKey: ["lot_batch_codes_with_stock"],
      })
    },
  })
}
